import { DOCUMENT, Injectable, NgZone, inject } from '@angular/core'
import { NavigationEnd, NavigationStart, Router } from '@angular/router'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { datadogRum } from '@datadog/browser-rum'
import {
  Observable,
  Subject,
  debounceTime,
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
  merge,
  skip,
  startWith,
  tap,
  timer,
} from 'rxjs'
import {
  FiltersChangeReason,
  LinkOpenMode,
  LinkTargetKind,
  OpenWindowContext,
  ResolvedTabIdentity,
  TabHandoff,
  TabProperties,
  TabTrackingEvent,
  TabTrackingEventName,
  TabTrackingTransport,
} from './tab-tracking.types'
import {
  TABS_CHANNEL_NAME,
  TAB_HANDOFF_STORAGE_KEY,
  TAB_ID_STORAGE_KEY,
  TAB_SEQ_STORAGE_KEY,
  TAB_UNLOAD_MARKER_KEY,
  computeNextTabSequence,
  describeLinkTarget,
  detectLinkOpenMode,
  extractRouteEntity,
  findSourceComponent,
  generateTabId,
  getFilterParams,
  getPathFromUrl,
  getQueryParam,
  hashFilters,
  isListRoute,
  normalizeRoute,
  parseQueryParams,
  parseStoredTabIdentity,
  parseTabHandoffs,
  parseTabSequence,
  parseUrlParts,
  pruneTabHandoffs,
  resolveTabIdentity,
  takeTabHandoff,
} from './tab-tracking.utils'

enum TabsChannelMessageType {
  Hello = 'hello',
  Heartbeat = 'heartbeat',
  Bye = 'bye',
}

interface TabsChannelMessage {
  type: TabsChannelMessageType
  tabId: string
}

interface LinkOpening {
  href: string
  mode: LinkOpenMode
  sourceComponent: string | null
}

/**
 * Events also sent as RUM actions. `tab_closed` is not in the list: RUM ends the view on
 * `beforeunload`, before `pagehide`, and drops actions that have no view, so its RUM action
 * is added on `beforeunload` instead (see `managePageLifecycle`).
 */
const RUM_ACTION_EVENTS: TabTrackingEventName[] = [
  TabTrackingEventName.LinkOpened,
]

/** Handoffs kept in `localStorage` at most */
const MAX_TAB_HANDOFFS = 20

/**
 * Gives every browser tab an identity and reports how tabs are opened, looked at and closed.
 *
 * It is app-agnostic: it does not send anything to Segment itself, it exposes `events$`
 * (consumed by `SegmentService`) and `getTabProperties()` (merged into every `page()` / `track()`),
 * and mirrors the tab identity into the Datadog RUM global context.
 */
@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class TabTrackingService {
  protected _document = inject<Document>(DOCUMENT)

  protected _router = inject(Router)

  protected _zone = inject(NgZone)

  /** Interval of the `BroadcastChannel` heartbeat */
  private readonly heartbeatIntervalMs = 15_000

  /** A tab not heard of for this long is considered gone (hidden tabs get throttled timers) */
  private readonly peerTimeoutMs = 90_000

  /** Focus signals (visibility, window focus / blur) are coalesced over this delay */
  private readonly focusSettleMs = 250

  /** A new tab intent (grid ctrl-click / context menu) applies to an `openWindow` call within this delay */
  private readonly openIntentMaxAgeMs = 1_000

  /** A filters change reason applies to a navigation within this delay */
  private readonly filtersReasonMaxAgeMs = 2_000

  private readonly _events$ = new Subject<TabTrackingEvent>()

  private window: Window | null

  /** Captured before any redirect strips them */
  private readonly landing: {
    search: string
    referrer: string
    hasOpener: boolean
  }

  private identity: ResolvedTabIdentity | null = null

  private channel: BroadcastChannel | null = null

  /** Other live tabs, by tab id, with the last time we heard from them */
  private peers = new Map<string, number>()

  private viewsInTab = 0

  private currentUrl = ''

  private focusedMs = 0

  private focusedSince: number | null = null

  private blurredAt: number | null = null

  private deepLink = false

  /** Left by the tab that opened this one */
  private handoff: TabHandoff | null = null

  private openIntent: {
    mode: LinkOpenMode
    sourceComponent: string
    at: number
  } | null = null

  private filtersReason: { reason: FiltersChangeReason; at: number } | null =
    null

  /** `popstate` for back / forward, `imperative` otherwise */
  private navigationTrigger: string | null = null

  private lastPathChangeAt = Date.now()

  private currentFiltersHash: string | null = null

  /** Last filters hash seen on each list route of this tab */
  private listFiltersHashes = new Map<string, string>()

  constructor() {
    this.window = this._document.defaultView
    this.landing = {
      search: this.window?.location?.search ?? '',
      referrer: this._document.referrer ?? '',
      hasOpener: !!this.window?.opener,
    }
  }

  /** Tab lifecycle, focus and navigation events */
  public get events$(): Observable<TabTrackingEvent> {
    return this._events$.asObservable()
  }

  private get tabId(): string | null {
    return this.identity?.id ?? null
  }

  /**
   * Resolves the tab identity and starts listening to the tab lifecycle. Idempotent.
   */
  public init(): void {
    if (this.identity || !this.window) {
      return
    }
    const now = Date.now()
    const sessionStorage = this.getStorage('sessionStorage')
    const navigationEntry = this.window.performance?.getEntriesByType?.(
      'navigation'
    )?.[0] as PerformanceNavigationTiming | undefined

    this.identity = resolveTabIdentity({
      stored: parseStoredTabIdentity(
        this.readStorage(sessionStorage, TAB_ID_STORAGE_KEY)
      ),
      hasUnloadMarker: !!this.readStorage(
        sessionStorage,
        TAB_UNLOAD_MARKER_KEY
      ),
      navigationType: navigationEntry?.type,
      documentStartedAt: this.window.performance?.timeOrigin ?? now,
      now,
      generateId: () => generateTabId(this.window?.crypto),
      nextSeq: () => this.nextTabSequence(now),
    })
    this.removeStorage(sessionStorage, TAB_UNLOAD_MARKER_KEY)
    this.writeStorage(
      sessionStorage,
      TAB_ID_STORAGE_KEY,
      JSON.stringify({
        id: this.identity.id,
        openedAt: this.identity.openedAt,
        seq: this.identity.seq,
      })
    )
    this.handoff = this.identity.isNew ? this.takeHandoff(now) : null

    this._zone.runOutsideAngular(() => {
      this.manageTabsChannel()
      this.manageFocus()
      this.managePageLifecycle()
      this.manageLinkClicks()
    })
    this.manageNavigation()
    this.updateRumContext()
  }

  /**
   * Properties merged into every analytics call. Empty until `init()` ran.
   * @returns {Partial<TabProperties>}
   */
  public getTabProperties(): Partial<TabProperties> {
    if (!this.identity) {
      return {}
    }
    return {
      tabId: this.identity.id,
      tabOpenedAt: new Date(this.identity.openedAt).toISOString(),
      tabSeq: this.identity.seq,
      rumSessionId: this.getRumSessionId(),
      tabsOpen: this.getTabsOpen(),
    }
  }

  /**
   * Flags the navigation in progress as coming from a deep link (notifications, mobile links).
   * Read by `tab_opened`.
   */
  public markDeepLink(): void {
    this.deepLink = true
  }

  /**
   * `window.open` reporting `link_opened`. Use it for every window / tab the app opens.
   * The mode is `window_open`, unless given in the context or announced by `markOpenIntent`.
   * @param {string | null | undefined} url
   * @param {string} target
   * @param {string} features
   * @param {OpenWindowContext} context
   * @returns {Window | null}
   */
  public openWindow(
    url: string | null | undefined,
    target = '_blank',
    features?: string,
    context: OpenWindowContext = {}
  ): Window | null {
    const intent = this.takeOpenIntent()
    this.reportLinkOpened(
      url ?? '',
      context.mode ?? intent?.mode ?? LinkOpenMode.WindowOpen,
      context.sourceComponent ?? intent?.sourceComponent ?? null
    )
    return this.window?.open(url as string, target, features) ?? null
  }

  /**
   * Announces that the next `openWindow` call comes from a user gesture the caller cannot see
   * (ctrl-click on a grid row, "open in a new tab" in the grid context menu)
   * @param {LinkOpenMode} mode
   * @param {string} sourceComponent
   */
  public markOpenIntent(mode: LinkOpenMode, sourceComponent: string): void {
    this.openIntent = { mode, sourceComponent, at: Date.now() }
  }

  /**
   * Announces why the filters in the URL are about to change. Without it, the change of
   * filters reported by `filters_changed` is attributed to the URL (back / forward, restore).
   * @param {FiltersChangeReason} reason
   */
  public noteFiltersChange(reason: FiltersChangeReason): void {
    this.filtersReason = { reason, at: Date.now() }
  }

  private manageNavigation(): void {
    this._router.events
      .pipe(
        filter(
          (event): event is NavigationStart | NavigationEnd =>
            event instanceof NavigationStart || event instanceof NavigationEnd
        ),
        tap((event) =>
          event instanceof NavigationStart
            ? (this.navigationTrigger = event.navigationTrigger ?? null)
            : this.onNavigationEnd(event.urlAfterRedirects)
        ),
        untilDestroyed(this)
      )
      .subscribe()
  }

  private onNavigationEnd(url: string, now = Date.now()): void {
    const pathChanged =
      this.viewsInTab === 0 ||
      getPathFromUrl(url) !== getPathFromUrl(this.currentUrl)
    const route = normalizeRoute(url)
    const filters = getFilterParams(parseQueryParams(url))
    const filtersHash = hashFilters(filters)

    this.viewsInTab += 1
    this.currentUrl = url
    if (this.viewsInTab === 1 && this.identity?.isNew) {
      this.emit(TabTrackingEventName.TabOpened, this.getTabOpenedProperties())
      this.reportBrowserMenuOpening()
    }
    if (pathChanged) {
      this.lastPathChangeAt = now
      if (isListRoute(route)) {
        this.emit(
          TabTrackingEventName.ListState,
          this.getListStateProperties(url, route, filters, filtersHash)
        )
      }
    } else if (filtersHash !== this.currentFiltersHash) {
      this.emit(TabTrackingEventName.FiltersChanged, {
        route,
        reason: this.takeFiltersChangeReason(now),
        filtersHash,
        filterCount: Object.keys(filters).length,
        msSinceNavigation: now - this.lastPathChangeAt,
      })
    }
    if (isListRoute(route)) {
      this.listFiltersHashes.set(route, filtersHash)
    }
    this.currentFiltersHash = filtersHash
    this.deepLink = false
    this.navigationTrigger = null
  }

  private getListStateProperties(
    url: string,
    route: string,
    filters: Record<string, string>,
    filtersHash: string
  ): Record<string, unknown> {
    const queryParams = parseQueryParams(url)
    const previousFiltersHash = this.listFiltersHashes.get(route)
    return {
      route,
      filtersHash,
      filterCount: Object.keys(filters).length,
      page: Number(queryParams['page']) || null,
      selectionCount: (queryParams['selected'] || '').split(',').filter(Boolean)
        .length,
      scrollRestored: this.navigationTrigger === 'popstate',
      revisit: previousFiltersHash !== undefined,
      stateKept:
        previousFiltersHash !== undefined
          ? previousFiltersHash === filtersHash
          : null,
    }
  }

  private takeFiltersChangeReason(now: number): FiltersChangeReason {
    const reason =
      this.filtersReason &&
      now - this.filtersReason.at <= this.filtersReasonMaxAgeMs
        ? this.filtersReason.reason
        : FiltersChangeReason.Url
    this.filtersReason = null
    return reason
  }

  private takeOpenIntent(): {
    mode: LinkOpenMode
    sourceComponent: string
  } | null {
    const intent =
      this.openIntent &&
      Date.now() - this.openIntent.at <= this.openIntentMaxAgeMs
        ? this.openIntent
        : null
    this.openIntent = null
    return intent
  }

  /**
   * Reports links opened in a new tab or window by the browser itself:
   * middle-click, ctrl / cmd / shift + click, `target="_blank"`.
   * The browser menu of a link ("Open link in new tab") fires no click: opening that menu on
   * an internal link only leaves a handoff, and the opened tab reports `link_opened`.
   */
  private manageLinkClicks(): void {
    const linkClicks$ = merge(
      fromEvent<MouseEvent>(this._document, 'click', { capture: true }),
      fromEvent<MouseEvent>(this._document, 'auxclick', { capture: true })
    ).pipe(
      map((event) => this.toLinkOpening(event)),
      filter((opening): opening is LinkOpening => !!opening),
      tap((opening) =>
        this.reportLinkOpened(
          opening.href,
          opening.mode,
          opening.sourceComponent
        )
      )
    )
    const browserMenus$ = fromEvent<MouseEvent>(this._document, 'contextmenu', {
      capture: true,
    }).pipe(
      map((event) => this.findLink(event)),
      filter((anchor): anchor is HTMLAnchorElement => !!anchor),
      tap((anchor) => this.onLinkBrowserMenu(anchor))
    )

    merge(linkClicks$, browserMenus$).pipe(untilDestroyed(this)).subscribe()
  }

  private findLink(event: MouseEvent): HTMLAnchorElement | null {
    const anchor = (event.target as Element | null)?.closest?.(
      'a[href]'
    ) as HTMLAnchorElement | null
    return anchor && /^https?:/i.test(anchor.href) ? anchor : null
  }

  private onLinkBrowserMenu(anchor: HTMLAnchorElement, now = Date.now()): void {
    const target = describeLinkTarget(
      anchor.href,
      this.window?.location?.origin ?? ''
    )
    if (target.targetKind !== LinkTargetKind.Internal || !target.targetPath) {
      return
    }
    this.leaveHandoff({
      path: target.targetPath,
      mode: LinkOpenMode.BrowserMenu,
      sourceTabId: this.tabId,
      sourceRoute: normalizeRoute(this.currentUrl),
      sourceComponent: findSourceComponent(anchor),
      at: now,
    })
  }

  /**
   * A tab opened from the browser menu of a link reports the `link_opened`
   * its opener could not see
   */
  private reportBrowserMenuOpening(): void {
    if (this.handoff?.mode !== LinkOpenMode.BrowserMenu) {
      return
    }
    this.emit(TabTrackingEventName.LinkOpened, {
      mode: LinkOpenMode.BrowserMenu,
      targetHost: this.window?.location?.host ?? null,
      targetKind: LinkTargetKind.Internal,
      targetRoute: normalizeRoute(this.currentUrl),
      sourceRoute: this.handoff.sourceRoute,
      sourceComponent: this.handoff.sourceComponent ?? null,
      sourceTabId: this.handoff.sourceTabId,
      ...extractRouteEntity(this.currentUrl),
    })
  }

  private toLinkOpening(event: MouseEvent): LinkOpening | null {
    const anchor = this.findLink(event)
    if (!anchor) {
      return null
    }
    const mode = detectLinkOpenMode(event, anchor.getAttribute('target'))
    return mode
      ? {
          href: anchor.href,
          mode,
          sourceComponent: findSourceComponent(anchor),
        }
      : null
  }

  private reportLinkOpened(
    href: string,
    mode: LinkOpenMode,
    sourceComponent: string | null,
    now = Date.now()
  ): void {
    const target = describeLinkTarget(
      String(href),
      this.window?.location?.origin ?? ''
    )
    const targetEntity = extractRouteEntity(target.targetPath ?? '')
    const sourceRoute = normalizeRoute(this.currentUrl)
    if (target.targetKind === LinkTargetKind.Internal && target.targetPath) {
      this.leaveHandoff({
        path: target.targetPath,
        mode,
        sourceTabId: this.tabId,
        sourceRoute,
        at: now,
      })
    }
    this.emit(TabTrackingEventName.LinkOpened, {
      mode,
      targetHost: target.targetHost,
      targetKind: target.targetKind,
      targetRoute: target.targetRoute,
      sourceRoute,
      sourceComponent,
      ...(targetEntity.entityId
        ? targetEntity
        : extractRouteEntity(this.currentUrl)),
    })
  }

  private leaveHandoff(handoff: TabHandoff): void {
    const storage = this.getStorage('localStorage')
    const handoffs = pruneTabHandoffs(
      parseTabHandoffs(this.readStorage(storage, TAB_HANDOFF_STORAGE_KEY)),
      handoff.at
    )
    this.writeStorage(
      storage,
      TAB_HANDOFF_STORAGE_KEY,
      JSON.stringify([...handoffs, handoff].slice(-MAX_TAB_HANDOFFS))
    )
  }

  private takeHandoff(now: number): TabHandoff | null {
    const storage = this.getStorage('localStorage')
    const { handoff, remaining } = takeTabHandoff(
      parseTabHandoffs(this.readStorage(storage, TAB_HANDOFF_STORAGE_KEY)),
      this.window?.location?.pathname ?? '',
      now
    )
    if (handoff) {
      this.writeStorage(
        storage,
        TAB_HANDOFF_STORAGE_KEY,
        JSON.stringify(remaining)
      )
    }
    return handoff
  }

  private getTabOpenedProperties(): Record<string, unknown> {
    const navigationEntry = this.window?.performance?.getEntriesByType?.(
      'navigation'
    )?.[0] as PerformanceNavigationTiming | undefined
    const referrer = parseUrlParts(this.landing.referrer)
    return {
      navigationType: navigationEntry?.type ?? null,
      referrerHost: referrer.host,
      referrerPath: referrer.path,
      hasOpener: this.landing.hasOpener,
      source: getQueryParam(this.landing.search, 'source'),
      deepLink: this.deepLink,
      openMode: this.handoff?.mode ?? null,
      openerTabId: this.handoff?.sourceTabId ?? null,
      openerRoute: this.handoff?.sourceRoute ?? null,
      copiedSession: !!this.identity?.copiedSession,
      landingRoute: normalizeRoute(this.currentUrl),
      ...extractRouteEntity(this.currentUrl),
    }
  }

  /**
   * Keeps the set of live tabs up to date through a `BroadcastChannel` heartbeat
   */
  private manageTabsChannel(): void {
    const BroadcastChannelRef = (
      this.window as Window & { BroadcastChannel?: typeof BroadcastChannel }
    ).BroadcastChannel
    if (!BroadcastChannelRef) {
      return
    }
    this.channel = new BroadcastChannelRef(TABS_CHANNEL_NAME)

    const messages$ = fromEvent<MessageEvent<TabsChannelMessage>>(
      this.channel,
      'message'
    ).pipe(
      map((event) => event.data),
      filter((message) => !!message?.tabId && message.tabId !== this.tabId),
      tap((message) => this.onChannelMessage(message))
    )
    const heartbeat$ = timer(0, this.heartbeatIntervalMs).pipe(
      tap((tick) =>
        this.postToChannel(
          tick === 0
            ? TabsChannelMessageType.Hello
            : TabsChannelMessageType.Heartbeat
        )
      )
    )

    merge(messages$, heartbeat$)
      .pipe(
        map(() => this.getTabsOpen()),
        distinctUntilChanged(),
        tap(() => this.updateRumContext()),
        untilDestroyed(this)
      )
      .subscribe()
  }

  private onChannelMessage(message: TabsChannelMessage): void {
    if (message.type === TabsChannelMessageType.Bye) {
      this.peers.delete(message.tabId)
      return
    }
    this.peers.set(message.tabId, Date.now())
    if (message.type === TabsChannelMessageType.Hello) {
      this.postToChannel(TabsChannelMessageType.Heartbeat)
    }
  }

  private postToChannel(type: TabsChannelMessageType): void {
    try {
      this.channel?.postMessage({ type, tabId: this.tabId })
    } catch {
      // A closed channel must never break the app
    }
  }

  /**
   * Emits `tab_focus` / `tab_blur`. Visibility alone misses alt-tab to another app,
   * window focus alone misses tab switches, so both are combined and coalesced.
   */
  private manageFocus(): void {
    const window = this.window as Window
    const initiallyFocused = this.isFocused()
    this.focusedSince = initiallyFocused ? Date.now() : null
    this.blurredAt = initiallyFocused ? null : Date.now()

    merge(
      fromEvent(this._document, 'visibilitychange'),
      fromEvent(window, 'focus'),
      fromEvent(window, 'blur')
    )
      .pipe(
        debounceTime(this.focusSettleMs),
        map(() => this.isFocused()),
        startWith(initiallyFocused),
        distinctUntilChanged(),
        skip(1),
        tap((focused) => (focused ? this.onFocus() : this.onBlur())),
        untilDestroyed(this)
      )
      .subscribe()
  }

  private isFocused(): boolean {
    return (
      this._document.visibilityState === 'visible' &&
      (this._document.hasFocus?.() ?? true)
    )
  }

  private onFocus(now = Date.now()): void {
    const hiddenMs = this.blurredAt !== null ? now - this.blurredAt : null
    this.focusedSince = now
    this.blurredAt = null
    this.emit(TabTrackingEventName.TabFocus, {
      ...this.getRouteProperties(),
      hiddenMs,
      tabsOpen: this.getTabsOpen(),
    })
  }

  private onBlur(now = Date.now()): void {
    this.focusedMs += this.focusedSince !== null ? now - this.focusedSince : 0
    this.focusedSince = null
    this.blurredAt = now
    this.emit(TabTrackingEventName.TabBlur, {
      ...this.getRouteProperties(),
      tabsOpen: this.getTabsOpen(),
    })
  }

  /**
   * `pagehide` (not `beforeunload`, which is unreliable and breaks the back/forward cache)
   * reports `tab_closed` to Segment; `pageshow` from the back/forward cache brings the tab back.
   * The RUM action is added on `beforeunload`, in the capture phase so that it runs before
   * the RUM SDK's own listener ends the view and flushes.
   */
  private managePageLifecycle(): void {
    const window = this.window as Window
    merge(
      fromEvent(window, 'beforeunload', { capture: true }).pipe(
        tap(() =>
          this.addRumAction(TabTrackingEventName.TabClosed, {
            ...this.getTabClosedProperties(),
            ...this.getTabProperties(),
          })
        )
      ),
      fromEvent<PageTransitionEvent>(window, 'pagehide').pipe(
        tap((event) => this.onPageHide(event))
      ),
      fromEvent<PageTransitionEvent>(window, 'pageshow').pipe(
        filter((event) => event.persisted),
        tap(() => this.onPageRestored())
      )
    )
      .pipe(untilDestroyed(this))
      .subscribe()
  }

  private onPageHide(event: PageTransitionEvent, now = Date.now()): void {
    this.writeStorage(
      this.getStorage('sessionStorage'),
      TAB_UNLOAD_MARKER_KEY,
      String(now)
    )
    this.postToChannel(TabsChannelMessageType.Bye)
    this.emit(
      TabTrackingEventName.TabClosed,
      { ...this.getTabClosedProperties(now), persisted: event.persisted },
      TabTrackingTransport.Beacon
    )
  }

  private getTabClosedProperties(now = Date.now()): Record<string, unknown> {
    return {
      tabId: this.tabId,
      openDurationMs: this.identity ? now - this.identity.openedAt : null,
      viewsInTab: this.viewsInTab,
      lastRoute: normalizeRoute(this.currentUrl),
      focusedMs:
        this.focusedMs +
        (this.focusedSince !== null ? now - this.focusedSince : 0),
    }
  }

  private onPageRestored(): void {
    this.removeStorage(this.getStorage('sessionStorage'), TAB_UNLOAD_MARKER_KEY)
    this.postToChannel(TabsChannelMessageType.Hello)
  }

  private emit(
    name: TabTrackingEventName,
    properties: Record<string, unknown>,
    transport = TabTrackingTransport.Default
  ): void {
    if (RUM_ACTION_EVENTS.includes(name)) {
      this.addRumAction(name, { ...properties, ...this.getTabProperties() })
    }
    this._events$.next({ name, properties, transport })
  }

  private getRouteProperties(): Record<string, unknown> {
    return {
      route: normalizeRoute(
        this.currentUrl || this.window?.location?.pathname || ''
      ),
      ...extractRouteEntity(this.currentUrl),
    }
  }

  private getTabsOpen(): number | null {
    if (!this.channel) {
      return null
    }
    const oldestAlive = Date.now() - this.peerTimeoutMs
    this.peers.forEach((lastSeen, tabId) => {
      if (lastSeen < oldestAlive) {
        this.peers.delete(tabId)
      }
    })
    return this.peers.size + 1
  }

  private nextTabSequence(now: number): number {
    const localStorage = this.getStorage('localStorage')
    const sequence = computeNextTabSequence(
      parseTabSequence(this.readStorage(localStorage, TAB_SEQ_STORAGE_KEY)),
      this.getRumSessionId(),
      now
    )
    this.writeStorage(
      localStorage,
      TAB_SEQ_STORAGE_KEY,
      JSON.stringify(sequence)
    )
    return sequence.seq
  }

  private getRumSessionId(): string | null {
    try {
      return datadogRum.getInternalContext?.()?.session_id ?? null
    } catch {
      return null
    }
  }

  private updateRumContext(): void {
    if (!this.identity) {
      return
    }
    const { tabId, tabOpenedAt, tabSeq, tabsOpen } = this.getTabProperties()
    try {
      datadogRum.setGlobalContextProperty('tab', {
        id: tabId,
        openedAt: tabOpenedAt,
        seq: tabSeq,
        tabsOpen,
      })
    } catch {
      // Monitoring must never break the app
    }
  }

  private addRumAction(name: string, context: Record<string, unknown>): void {
    try {
      datadogRum.addAction(name, context)
    } catch {
      // Monitoring must never break the app
    }
  }

  private getStorage(kind: 'sessionStorage' | 'localStorage'): Storage | null {
    try {
      return this.window?.[kind] ?? null
    } catch {
      return null
    }
  }

  private readStorage(storage: Storage | null, key: string): string | null {
    try {
      return storage?.getItem(key) ?? null
    } catch {
      return null
    }
  }

  private writeStorage(
    storage: Storage | null,
    key: string,
    value: string
  ): void {
    try {
      storage?.setItem(key, value)
    } catch {
      // Storage can be full or disabled
    }
  }

  private removeStorage(storage: Storage | null, key: string): void {
    try {
      storage?.removeItem(key)
    } catch {
      // Storage can be disabled
    }
  }
}
